import { PubSub } from '../PubSub';
import type { GameEvents } from '../events';
import { WorldModel, ToolType } from '../models/WorldModel';
import { DeadState } from './states/being/DeadState';

type ToolEvent = GameEvents['world:tool-changed'];

/** Display label and tool-changed event name for each tool exposed by a level. */
const TOOL_DEFINITIONS: Map<ToolType, { label: string; event: ToolEvent }> = new Map([
    [ToolType.Raise,      { label: 'Raise Terrain',   event: 'raise' }],
    [ToolType.Lower,      { label: 'Lower Terrain',   event: 'lower' }],
    [ToolType.Flatten,    { label: 'Flatten Terrain', event: 'flatten' }],
    [ToolType.SpawnBeast, { label: 'Spawn Beast',     event: 'spawn-beast' }],
]);

export class UIManager {
    private beingStatsDiv: HTMLElement | null;
    private statValueDiv: HTMLElement | null;
    private statFillDiv: HTMLElement | null;
    private toolPanel: HTMLElement | null;
    private tipsPanel: HTMLElement | null;
    private tipTextDiv: HTMLElement | null;
    private toolButtons: Map<ToolType, HTMLButtonElement> = new Map();

    constructor(
        private worldModel: WorldModel,
        private pubsub: PubSub<GameEvents>
    ) {
        this.beingStatsDiv = document.getElementById('being-stats');
        this.statValueDiv = document.getElementById('social-cohesion');
        this.statFillDiv = document.getElementById('social-cohesion-fill');
        this.toolPanel = document.getElementById('tool-panel');
        this.tipsPanel = document.getElementById('tips-panel');
        this.tipTextDiv = document.getElementById('tip-text');

        this.renderToolButtons();

        this.pubsub.subscribe('level:started', () => this.renderToolButtons());

        this.pubsub.subscribe('level:tip', (text) => {
            if (this.tipTextDiv) this.tipTextDiv.textContent = text;
            if (this.tipsPanel) this.tipsPanel.style.display = '';
        });

        document.getElementById('tip-close')?.addEventListener('click', () => {
            if (this.tipsPanel) this.tipsPanel.style.display = 'none';
        });
    }

    /**
     * Rebuilds the tool panel from the current level's `tools` array. The first
     * enabled tool is selected so the model and UI stay in sync.
     */
    private renderToolButtons(): void {
        if (!this.toolPanel) return;

        this.toolPanel.innerHTML = '';
        this.toolButtons.clear();

        for (const tool of this.worldModel.level.tools) {
            const def = TOOL_DEFINITIONS.get(tool);
            if (!def) continue;

            const button = document.createElement('button');
            button.textContent = def.label;
            button.addEventListener('click', () => this.selectTool(tool));

            this.toolPanel.appendChild(button);
            this.toolButtons.set(tool, button);
        }

        this.refreshToolAvailability();

        const firstEnabled = [...this.toolButtons.keys()].find(tool => this.isToolEnabled(tool));
        if (firstEnabled !== undefined) this.selectTool(firstEnabled);
    }

    /** Whether the current level currently permits `tool`. Levels that don't
     *  implement `isToolEnabled` have every tool always available. */
    private isToolEnabled(tool: ToolType): boolean {
        const level = this.worldModel.level;
        return level.isToolEnabled ? level.isToolEnabled(tool) : true;
    }

    /** Makes `tool` the active tool, updating both the model (via the event) and the
     *  button highlight. */
    private selectTool(tool: ToolType): void {
        const def = TOOL_DEFINITIONS.get(tool);
        if (!def) return;

        this.pubsub.publish('world:tool-changed', def.event);
        for (const [candidate, button] of this.toolButtons) {
            button.classList.toggle('active', candidate === tool);
        }
    }

    /**
     * Syncs each button's disabled state with the level's `isToolEnabled`. If the
     * tool that just became unavailable is the selected one, the selection moves to
     * the first enabled tool — otherwise `worldModel.currentTool` would keep pointing
     * at a greyed-out tool and clicks on the map would still apply it.
     */
    private refreshToolAvailability(): void {
        let selectedIsDisabled = false;

        for (const [tool, button] of this.toolButtons) {
            const enabled = this.isToolEnabled(tool);
            button.disabled = !enabled;
            if (!enabled && this.worldModel.currentTool === tool) {
                selectedIsDisabled = true;
            }
        }

        if (!selectedIsDisabled) return;

        const fallback = [...this.toolButtons.keys()].find(tool => this.isToolEnabled(tool));
        if (fallback !== undefined) this.selectTool(fallback);
    }

    public updateBeingStats(): void {
        const level = this.worldModel.level;

        this.refreshToolAvailability();

        if (level.stats[0]) {
            const stat = level.stats[0];
            if (this.statValueDiv) {
                this.statValueDiv.textContent = `${stat.label}: ${stat.getValue()}`;
            }
            if (this.statFillDiv && stat.getFraction) {
                this.statFillDiv.style.width = `${stat.getFraction() * 100}%`;
            }
        }

        if (!this.beingStatsDiv) return;

        const beings = this.worldModel.beings;

        if (beings.length === 0) {
            this.beingStatsDiv.innerHTML = 'SEARCHING...';
            return;
        }

        this.beingStatsDiv.innerHTML = `POPULATION: ${beings.filter(b => b.state !== DeadState.stateName).length}`;
    }
}
